import {createElement, FC, MutableRefObject, ReactElement, useState} from 'react';
import './index-view-screen.scss';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {Alert, Box, Stack} from '@mui/material';
import {LoadingContent} from '../loading-content/loading-content';
import {ViewTypeToggler} from '../view-type-toggler/view-type-toggler';
import SearchInput from '../search-input/search-input';
import {PaginatedTableItem} from '../../interfaces/paginated-table-data';
import {AffiliatedUser} from '../../backend/responses/affiliated-user';
import {ScrollInstance} from '../../hooks/infinite-scroll-items';
import GenericHeader from '../generic-header/generic-header';
import {IndexViewResults} from '../index-view-results/index-view-results';
import {UsersFilters} from '../users-filters';
import {Menu, Apps} from '@mui/icons-material';
import {LocalStorageKey} from '../../enums';
import {ComboActionsProps} from '../combo-actions/combo-actions';
import {UserType} from '../../backend/enums/user-type';

/*
 * HOC for users index screens
 */
interface IndexViewScreenProps {
  /* page specific props */
  pageHeader?: {
    screenTitle?: string;
    searchLabel?: string;
    rightButton?: {
      label: string;
      toLink: string;
      endIcon: ReactElement;
    };
  };

  /* page query props */
  searchOptions: {
    searchString?: boolean;
    toggleDisplay?: boolean;
    filters?: boolean;
  };

  /* page UI related props */
  loaderRef: MutableRefObject<HTMLButtonElement>;
  dataScrollInstance: ScrollInstance<PaginatedTableItem<AffiliatedUser>>;
  rightColumn: FC<{resultsLength: number}>;
  emptyStateMessage: {title: string; subtitle: string};
  renderItem: {
    list?: {
      element: (data: any) => ReactElement;
      skeleton: () => ReactElement;
    };
    grid?: {
      element: (data: any) => ReactElement;
      skeleton: () => ReactElement;
      gridItemWidth: number;
    };
  };
  canWrite: boolean;
  canDelete: boolean;
  actions?: ComboActionsProps;
  viewType?: UserType;
}

export type IndexScreenViewType = 'list' | 'grid';

export const IndexViewScreen = ({
  pageHeader,
  searchOptions,
  dataScrollInstance,
  emptyStateMessage,
  rightColumn,
  renderItem,
  canWrite,
  canDelete,
  loaderRef,
  actions,
  viewType,
}: IndexViewScreenProps) => {
  const [activeViewType, setActiveViewType] = useState<IndexScreenViewType>(getInitialViewMode);

  const {
    data,
    setData,
    firstLoad,
    enabled,
    isLoading,
    error: fetchError,
    hasNextPage,
    handleApplyFilters,
    handleUpdateCurrentSearch,
    handleApplySort,
    currentPage,
    currentSort,
    currentFilters,
    counter,
    currentSearch,
  } = dataScrollInstance;

  const {t} = useTranslation();

  const noSearchResultsMessage = {
    title: t(translations.index_grid_screen.empty_search_message.title, {name: currentSearch}),
    subtitle: t(translations.index_grid_screen.empty_search_message.subtitle),
  };

  const noFilteredResultsMessage = {
    title: t(translations.index_grid_screen.empty_filters_message.title),
    subtitle: t(translations.index_grid_screen.empty_filters_message.subtitle),
  };

  return (
    <Stack rowGap={2}>
      <GenericHeader
        isLoading={!firstLoad}
        pageTitle={pageHeader?.screenTitle || ''}
        leftColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              padding: 0,
              width: '100%',
            }}
          >
            {searchOptions && (
              <>
                {searchOptions.searchString && (
                  <SearchInput
                    placeholder={t(translations.index_grid_screen.search_placeholder)}
                    onSearchAction={handleUpdateCurrentSearch}
                  />
                )}
                {searchOptions.filters && (
                  <UsersFilters
                    hideShifts
                    hideTaskGroups
                    hideOccurrences
                    onChange={handleApplyFilters}
                  />
                )}
                {searchOptions.toggleDisplay && Object.keys(renderItem).length === 2 && (
                  <ViewTypeToggler
                    value={activeViewType}
                    options={[
                      {
                        id: 'grid',
                        icon: Apps,
                        name: t(translations.index_grid_screen.toggler.grid),
                      },
                      {
                        id: 'list',
                        icon: Menu,
                        name: t(translations.index_grid_screen.toggler.list),
                      },
                    ]}
                    onClick={(val) => {
                      localStorage.setItem(LocalStorageKey.SeniorsListDisplay, val);
                      setActiveViewType(val as IndexScreenViewType);
                    }}
                  />
                )}
              </>
            )}
          </Stack>
        }
        rightColumn={
          <>
            {createElement<{resultsLength: number}>(rightColumn, {
              resultsLength: counter ?? 0,
            })}
          </>
        }
        actions={actions}
      />

      <Stack component="section" className="index-view-screen-results-wrapper">
        {/*PANEL CONTAINER HERE */}
        <Box className="index-view-screen-results-container">
          <IndexViewResults
            {...{
              firstLoad,
              enabled,
              isLoading,
              activeViewType,
              data,
              setData,
              renderItem,
              handleApplySort,
              currentSearch,
              currentSort,
              currentFilters,
              currentPage,
              emptyStateMessage,
              noSearchResultsMessage,
              noFilteredResultsMessage,
              canWrite,
              canDelete,
              viewType,
            }}
          />

          {!isLoading && hasNextPage && (
            <LoadingContent
              ref={loaderRef}
              className="index-view-screen-results-load-more-spinner"
              size={24}
            />
          )}

          {fetchError && <Alert severity="error">{fetchError}</Alert>}
        </Box>
      </Stack>
    </Stack>
  );
};

// --------------------------------------------------------------------------
// --------------------------------------------------------------------------
// --------------------------------------------------------------------------

function getInitialViewMode(): IndexScreenViewType {
  // Use local storage if available
  const storedDisplay = localStorage.getItem(
    LocalStorageKey.SeniorsListDisplay
  ) as IndexScreenViewType | null;

  switch (storedDisplay) {
    case 'grid':
    case 'list':
      return storedDisplay;
    default:
      return 'grid';
  }
}
